import { Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Cpu } from "lucide-react";
import AiModelsClient from "@/components/admin/ai-models/AiModelsClient";
import AiCostsClient from "@/components/admin/ai-models/AiCostsClient";
import LearnerCostsClient from "@/components/admin/ai-models/LearnerCostsClient";

const TABS = [
  { id: "models", label: "Models", subtitle: "Which model each content-generation task uses, and how hard it reasons." },
  { id: "costs", label: "Costs", subtitle: "See what AI generation costs, and look into any single call." },
  { id: "learners", label: "Learners", subtitle: "See what each learner's AI generation costs, and who costs the most." },
] as const;

export default function AdminAiModelsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTabId = searchParams.get("tab") || "models";
  const activeTab = TABS.find((t) => t.id === currentTabId) || TABS[0];

  const handleTabChange = (tabId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tabId === "models") {
        next.delete("tab");
      } else {
        next.set("tab", tabId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-8">
      <div className="sticky top-0 z-20 -mx-4 border-b border-slate-200 bg-white px-4 pt-3 shadow-sm sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <Cpu className="h-4 w-4" />
          </div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600">
            AI Models & Cost Controls
          </h1>
        </div>
        <p className="mt-1 text-sm text-slate-500 font-medium leading-tight">{activeTab.subtitle}</p>
        <nav className="-mb-px mt-3 flex gap-5" aria-label="AI models sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleTabChange(t.id)}
              aria-current={t.id === activeTab.id ? "page" : undefined}
              className={`border-b-2 pb-2.5 text-sm font-semibold transition ${
                t.id === activeTab.id
                  ? "border-indigo-600 text-indigo-700"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab.id === "costs" ? (
          <Suspense fallback={<div className="p-8 text-center text-slate-400">Loading AI costs...</div>}>
            <AiCostsClient />
          </Suspense>
        ) : activeTab.id === "learners" ? (
          <Suspense fallback={<div className="p-8 text-center text-slate-400">Loading learner costs...</div>}>
            <LearnerCostsClient />
          </Suspense>
        ) : (
          <Suspense fallback={<div className="p-8 text-center text-slate-400">Loading models catalog...</div>}>
            <AiModelsClient />
          </Suspense>
        )}
      </div>
    </div>
  );
}
